import { Stack, TextField, MenuItem, InputAdornment, Button } from '@mui/material';
import { Search, FilterAltOff } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { lookupsApi } from '../api/lookups.api';
import { clientsApi } from '../api/clients.api';
import { dealersApi } from '../api/dealers.api';

export type FilterKey = 'search' | 'status' | 'month' | 'dateFrom' | 'dateTo' | 'region' | 'state' | 'client' | 'dealer';

export interface FilterValue {
  search?: string;
  status?: string;
  month?: number | '';
  dateFrom?: string;
  dateTo?: string;
  regionId?: number | '';
  stateId?: number | '';
  clientId?: number | '';
  dealerId?: number | '';
}

export const EMPTY_FILTERS: FilterValue = { search: '', status: '', month: '', dateFrom: '', dateTo: '', regionId: '', stateId: '', clientId: '', dealerId: '' };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface Props {
  value: FilterValue;
  onChange: (v: FilterValue) => void;
  show: FilterKey[];
  statusOptions?: string[];
}

/** Reusable PDI filter toolbar (Date / Month / Status / Region / State / Client / Dealer / Search). */
export function ListFilters({ value, onChange, show, statusOptions = [] }: Props) {
  const has = (k: FilterKey) => show.includes(k);
  const set = <K extends keyof FilterValue>(k: K, v: FilterValue[K]) => onChange({ ...value, [k]: v });

  const lookups = useQuery({ queryKey: ['lookups'], queryFn: () => lookupsApi.all(), enabled: has('region') || has('state'), staleTime: 5 * 60 * 1000 });
  const clients = useQuery({ queryKey: ['clients', 'filter'], queryFn: () => clientsApi.list({ limit: 500 }), enabled: has('client'), staleTime: 5 * 60 * 1000 });
  const dealers = useQuery({ queryKey: ['dealers', 'filter'], queryFn: () => dealersApi.list({ limit: 500 }), enabled: has('dealer'), staleTime: 5 * 60 * 1000 });

  const states = (lookups.data?.states ?? []).filter((s) => !value.regionId || s.regionId === value.regionId);
  const active = Object.values(value).some((x) => x !== '' && x != null);

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }} alignItems="center">
      {has('search') && (
        <TextField size="small" placeholder="Search…" value={value.search ?? ''} onChange={(e) => set('search', e.target.value)} sx={{ minWidth: 200 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
      )}
      {has('status') && (
        <TextField select size="small" label="Status" value={value.status ?? ''} onChange={(e) => set('status', e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="">All Status</MenuItem>
          {statusOptions.map((s) => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s.replace(/_/g, ' ')}</MenuItem>)}
        </TextField>
      )}
      {has('month') && (
        <TextField select size="small" label="Month" value={value.month ?? ''} onChange={(e) => set('month', e.target.value === '' ? '' : Number(e.target.value))} sx={{ minWidth: 120 }}>
          <MenuItem value="">All Months</MenuItem>
          {MONTHS.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
        </TextField>
      )}
      {has('dateFrom') && <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={value.dateFrom ?? ''} onChange={(e) => set('dateFrom', e.target.value)} sx={{ minWidth: 150 }} />}
      {has('dateTo') && <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={value.dateTo ?? ''} onChange={(e) => set('dateTo', e.target.value)} sx={{ minWidth: 150 }} />}
      {has('region') && (
        <TextField select size="small" label="Region" value={value.regionId ?? ''} onChange={(e) => onChange({ ...value, regionId: e.target.value === '' ? '' : Number(e.target.value), stateId: '' })} sx={{ minWidth: 150 }}>
          <MenuItem value="">All Regions</MenuItem>
          {(lookups.data?.regions ?? []).map((r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
        </TextField>
      )}
      {has('state') && (
        <TextField select size="small" label="State" value={value.stateId ?? ''} onChange={(e) => set('stateId', e.target.value === '' ? '' : Number(e.target.value))} sx={{ minWidth: 150 }}>
          <MenuItem value="">All States</MenuItem>
          {states.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
        </TextField>
      )}
      {has('client') && (
        <TextField select size="small" label="Client" value={value.clientId ?? ''} onChange={(e) => set('clientId', e.target.value === '' ? '' : Number(e.target.value))} sx={{ minWidth: 180 }}>
          <MenuItem value="">All Clients</MenuItem>
          {(clients.data?.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.clientName}</MenuItem>)}
        </TextField>
      )}
      {has('dealer') && (
        <TextField select size="small" label="Dealer" value={value.dealerId ?? ''} onChange={(e) => set('dealerId', e.target.value === '' ? '' : Number(e.target.value))} sx={{ minWidth: 180 }}>
          <MenuItem value="">All Dealers</MenuItem>
          {(dealers.data?.data ?? []).map((d) => <MenuItem key={d.id} value={d.id}>{d.dealerName}</MenuItem>)}
        </TextField>
      )}
      {active && <Button size="small" startIcon={<FilterAltOff />} onClick={() => onChange({ ...EMPTY_FILTERS })}>Clear</Button>}
    </Stack>
  );
}

/** Build server query params for aggregate dashboards (month → date range of the given year). */
export function toDashboardParams(v: FilterValue, year = new Date().getFullYear()): { from?: string; to?: string; regionId?: number; dealerId?: number; status?: string } {
  let from = v.dateFrom || undefined;
  let to = v.dateTo || undefined;
  if (!from && !to && v.month) {
    const mm = String(v.month).padStart(2, '0');
    from = `${year}-${mm}-01`;
    to = `${year}-${mm}-${String(new Date(year, v.month, 0).getDate()).padStart(2, '0')}`;
  }
  return { from, to, regionId: v.regionId || undefined, dealerId: v.dealerId || undefined, status: v.status || undefined };
}

/** Client-side predicate — pass accessors for the fields a row exposes. */
export function matchFilters(
  v: FilterValue,
  acc: { text?: string; status?: string | null; date?: string | null; regionId?: number | null; stateId?: number | null; clientId?: number | null; dealerId?: number | null },
): boolean {
  if (v.search && !(acc.text ?? '').toLowerCase().includes(v.search.toLowerCase())) return false;
  if (v.status && acc.status !== v.status) return false;
  if (v.regionId && acc.regionId !== v.regionId) return false;
  if (v.stateId && acc.stateId !== v.stateId) return false;
  if (v.clientId && acc.clientId !== v.clientId) return false;
  if (v.dealerId && acc.dealerId !== v.dealerId) return false;
  if ((v.month || v.dateFrom || v.dateTo)) {
    const d = acc.date ? acc.date.slice(0, 10) : null;
    if (!d) return false;
    if (v.month && Number(d.slice(5, 7)) !== v.month) return false;
    if (v.dateFrom && d < v.dateFrom) return false;
    if (v.dateTo && d > v.dateTo) return false;
  }
  return true;
}
